import { readContract, simulateContract, writeContract } from '@wagmi/core'
import {
  Address,
  Hex,
  PublicClient,
  encodeAbiParameters,
  encodeFunctionData,
  encodePacked,
  isAddressEqual,
  keccak256,
  padHex,
  parseAbiParameters,
  parseUnits,
  toHex,
  zeroAddress,
} from 'viem'
import { ConfigBuilderAbi } from 'abis/ConfigBuilderAbi'
import { HomechainOmnichainMessengerAbi } from 'abis/HomechainOmnichainMessagerAbi'
import { IndexV2Abi } from 'abis/IndexV2'
import { NotificationActionTypes, notifyTransactionError } from 'components'
import { SuccessPayload } from 'components/widgets/invest-component/storage/invest-states.js'
import { TransactionResult } from 'services/query-v2/deposit-redeem-v1/utils'
import { viemPublicClients, wagmiConfig } from 'services/wallet/chains'
import { ParsedConfigs } from '..'
import { ZeroExAggregator } from '../aggregators/0x-aggregators'
import { bridgingCurrencies, getBridging } from '../bridging-currencies'
import { additionalRemoteContracts, deployments } from '../deployments'
import { BridgeParamsAbi } from '../redeem/additional-abis/bridge-params'
import { EscrowHelperAbi } from '../redeem/additional-abis/escrow-helpers'
import { LzHelperAbi } from '../redeem/additional-abis/lz-helper'
import { StargateRouterAbi } from '../redeem/additional-abis/sg-helper'
import { MessageCallbackAbi } from '../redeem/additional-abis/trades'
import { HOMECHAIN_CALLBACK_BUILDER, REMOTE_CALLBACK_BUILDER } from '../redeem/callback-helpers'
import { escrowHelper } from '../redeem/configs'
import { Target, Trade } from '../redeem/interfaces'
import { LZ_HELPER, estimateLzFees, lzLookup } from '../redeem/lz-helpers'
import { zeroTarget } from '../redeem/utils'
import { createAirdropOptions, toChainId } from '../utils'
import { swap } from '../pendle-utils'

export type Retry = {
  eid: number
  options: Hex[]
  callbacks: Hex[]
}

export const retry = async (
  client: PublicClient,
  configs: ParsedConfigs,
  props: SuccessPayload,
) => {
  let response: TransactionResult | undefined
  const chainIds = Object.keys(configs.assets).map(Number)
  const { account, stopProcessing, clearState, inputCurrency, productAddress } = props
  const homeClient = viemPublicClients[inputCurrency.chainId]

  const {
    EscrowDeployer,
    DefaultEscrow,
    ConfigBuilder,
    Messenger: HomeMessenger,
  } = deployments[inputCurrency.chainId][productAddress]

  try {
    const { activeChainIds } = await homeClient.readContract({
      address: ConfigBuilder,
      abi: ConfigBuilderAbi,
      functionName: 'getState',
    })

    const [homeChainId, ...remoteChainIds] = activeChainIds.map((c) => toChainId(c, chainIds))

    const aggregator = ZeroExAggregator.builder.build()

    const { eid: homeEid } = lzLookup[homeChainId]

    const [homeBalances, escrowDeployed] = await readContract(wagmiConfig, {
      address: escrowHelper[homeChainId],
      abi: EscrowHelperAbi,
      functionName: 'userBalancesOf',
      args: [
        account,
        EscrowDeployer,
        productAddress,
        [...new Set(remoteChainIds.map((c) => getBridging(homeChainId, c)))],
      ],
    })

    const retries: Retry[] = []
    let value = 0n

    const outputAsset: Address = configs.reserveAsset

    for (const dstChainId of remoteChainIds) {
      const remoteClient = viemPublicClients[dstChainId]
      const { Vault, Messenger: RemoteMessenger } =
        additionalRemoteContracts[productAddress][dstChainId]

      const bridgingCurrency = getBridging(dstChainId, homeChainId)

      const [{ currencies, currencyBalances, snapshots, snapshotBalances }, escrow] =
        await remoteClient.readContract({
          address: escrowHelper[dstChainId],
          abi: EscrowHelperAbi,
          functionName: 'userBalancesOf',
          args: [account, EscrowDeployer, Vault, []],
        })

      const trades: Trade[] = []
      for (const i in currencies) {
        const currency = currencies[i]
        const currencyBalance = currencyBalances[i]

        if (isAddressEqual(currency, bridgingCurrency) || currencyBalance < parseUnits('1', 6))
          continue

        const quote = await aggregator.quote(
          dstChainId,
          currency,
          bridgingCurrency,
          (currencyBalance * 101n) / 100n,
          escrow,
        )

        trades.push({
          skipRevert: true,
          currency,
          allowanceTarget: quote.allowanceTarget,
          addr: quote.to,
          value: quote.value,
          data: quote.data,
        })
      }

      const additionalWithdrawals: Trade[] = snapshots.map((snapshot, i) => ({
        skipRevert: true,
        currency: bridgingCurrency,
        allowanceTarget: zeroAddress,
        addr: Vault,
        value: 0n,
        data: encodeFunctionData({
          abi: IndexV2Abi,
          functionName: 'withdraw',
          args: [snapshot, snapshotBalances[i], escrow],
        }),
      }))

      const callbacks = [
        encodeAbiParameters(MessageCallbackAbi, [
          [...additionalWithdrawals, ...trades],
          zeroTarget,
        ]),
      ]

      const bridgingEstimationData = [
        encodeAbiParameters(parseAbiParameters('uint8, address, bytes'), [
          2,
          account,
          callbacks[0],
        ]),
      ]

      const { result: bridgingAmount } = await remoteClient.simulateContract({
        address: escrowHelper[dstChainId],
        abi: EscrowHelperAbi,
        functionName: 'estimateBridgingAmount',
        args: [RemoteMessenger, bridgingEstimationData, escrow, bridgingCurrency],
        stateOverride: [
          {
            address: RemoteMessenger,
            stateDiff: [
              {
                slot: toHex(1, { size: 32 }),
                value: padHex(escrowHelper[dstChainId], { size: 32 }),
              },
            ],
          },
        ],
      })

      if (bridgingAmount === 0n) continue
      const homeBridgingCurrency = getBridging(homeChainId, dstChainId)
      const quote = await aggregator.quote(
        homeChainId,
        homeBridgingCurrency,
        outputAsset,
        (bridgingAmount * 101n) / 100n,
        escrow,
      )

      const homeTradesBridging: Trade[] = [
        {
          skipRevert: false,
          currency: homeBridgingCurrency,
          allowanceTarget: quote.allowanceTarget,
          addr: quote.to,
          value: quote.value,
          data: quote.data,
        },
      ]

      const homeTradesCallback: Target = {
        skipRevert: true,
        addr: HOMECHAIN_CALLBACK_BUILDER[homeChainId],
        value: 0n,
        data: encodeAbiParameters(parseAbiParameters('address, address'), [outputAsset, account]),
      }

      let homechainCallbacksData = encodeAbiParameters(MessageCallbackAbi, [
        homeTradesBridging,
        homeTradesCallback,
      ])

      let { result: homeGas } = await homeClient.simulateContract({
        address: escrowHelper[homeChainId],
        abi: EscrowHelperAbi,
        functionName: 'estimateHomeMessageGas',
        args: [
          escrowDeployed ? account : zeroAddress,
          EscrowDeployer,
          homeBridgingCurrency,
          bridgingAmount,
          homechainCallbacksData,
          3n,
        ],
        stateOverride: [
          {
            address: escrowDeployed ? escrow : DefaultEscrow,
            stateDiff: [
              {
                slot: toHex(1, { size: 32 }),
                value: padHex(escrowHelper[homeChainId], { size: 32 }),
              },
            ],
          },
          {
            address: homeBridgingCurrency,
            stateDiff: [
              {
                slot: keccak256(
                  encodeAbiParameters(parseAbiParameters('address user, uint slot'), [
                    escrowDeployed ? escrow : DefaultEscrow,
                    BigInt(51),
                  ]),
                ),
                value: padHex(toHex(bridgingAmount), { size: 32 }),
              },
            ],
          },
        ],
      })

      homeGas = (homeGas * 110n) / 100n

      homeTradesBridging[0].skipRevert = true
      homechainCallbacksData = encodeAbiParameters(MessageCallbackAbi, [
        homeTradesBridging,
        homeTradesCallback,
      ])

      const lzFees = await remoteClient.readContract({
        address: lzLookup[homeChainId].sgComposer,
        abi: StargateRouterAbi,
        functionName: 'quoteLayerZeroFee',
        args: [
          homeEid,
          1,
          HomeMessenger,
          homechainCallbacksData,
          {
            dstGasForCall: homeGas,
            dstNativeAmount: 0n,
            dstNativeAddr: HomeMessenger,
          },
        ],
      })

      const remoteAirdropAmount = (lzFees[0] * 110n) / 100n

      const [airdropAmountInNative] = await readContract(wagmiConfig, {
        address: LZ_HELPER[homeChainId],
        abi: LzHelperAbi,
        functionName: 'convertToDstNative',
        args: [HomeMessenger, [remoteAirdropAmount], [lzLookup[dstChainId].eid]],
      })

      const bridgeParams = {
        currency: bridgingCurrency,
        sgRouter: lzLookup[dstChainId].sgComposer,
        refundAddress: escrow,
        srcPoolId: BigInt(bridgingCurrencies[dstChainId].poolId),
        dstPoolId: BigInt(bridgingCurrencies[homeChainId].poolId),
        homeGas,
        packedRecipient: encodePacked(['address'], [escrow]),
        payload: homechainCallbacksData,
        dstEid: homeEid,
        maxSlippageBP: 300,
      }

      const target: Target = {
        skipRevert: true,
        addr: REMOTE_CALLBACK_BUILDER[dstChainId],
        value: 0n,
        data: encodeAbiParameters([BridgeParamsAbi], [bridgeParams]),
      }
      callbacks[0] = encodeAbiParameters(MessageCallbackAbi, [
        [...additionalWithdrawals, ...trades],
        target,
      ])

      const estimationData = callbacks.map((c) =>
        encodeAbiParameters(parseAbiParameters('uint8, address, bytes'), [2, account, c]),
      )

      const nativeIndex = currencies.indexOf(zeroAddress)
      const currentNativeBalance = nativeIndex >= 0 ? currencyBalances[nativeIndex] : 0n

      let { result: callbacksGas } = await remoteClient.simulateContract({
        address: escrowHelper[dstChainId],
        abi: EscrowHelperAbi,
        functionName: 'estimateMessagesGas',
        args: [RemoteMessenger, estimationData],
        stateOverride: [
          {
            address: RemoteMessenger,
            stateDiff: [
              {
                slot: toHex(1, { size: 32 }),
                value: padHex(escrowHelper[dstChainId], { size: 32 }),
              },
            ],
          },
          {
            address: escrow,
            balance: currentNativeBalance + remoteAirdropAmount,
          },
        ],
      })

      callbacksGas = callbacksGas.map((c) => (c * 115n) / 100n)

      const airdropOptions = await Promise.all(
        callbacks.map(async (callback, i) => {
          const options = createAirdropOptions(
            callbacksGas[i],
            i === callbacks.length - 1 ? remoteAirdropAmount : 0n,
            escrow,
          )
          const [fee] = await estimateLzFees({
            client: homeClient,
            dstChainId,
            uaContractAddress: HomeMessenger,
            payload: callback,
            options,
          })

          value += fee

          return options
        }),
      )

      value += airdropAmountInNative

      retries.push({
        eid: lzLookup[dstChainId].eid,
        callbacks: callbacks,
        options: airdropOptions,
      })
    }

    const homeTrades: Trade[] = (
      await Promise.all(
        homeBalances.currencies.map(async (currencyAddress, i) => {
          if (isAddressEqual(currencyAddress, outputAsset)) return []
          return swap(
            wagmiConfig,
            currencyAddress,
            homeBalances.currencyBalances[i],
            outputAsset,
            zeroAddress,
            account,
            100n,
          )
        }),
      )
    ).flat()

    const escrowParams = {
      skipRevert: true,
      addr: HOMECHAIN_CALLBACK_BUILDER[homeChainId],
      value: 0n,
      data: encodeAbiParameters(parseAbiParameters('address, address'), [outputAsset, account]),
    }

    if (stopProcessing) stopProcessing()

    const { request } = await simulateContract(wagmiConfig, {
      account,
      address: HomeMessenger,
      value,
      abi: HomechainOmnichainMessengerAbi,
      functionName: 'retryRedeem',
      args: [homeTrades, escrowParams, retries, account, zeroAddress],
      stateOverride: [
        {
          address: account,
          balance: value,
        },
      ],
    })

    const hash = await writeContract(wagmiConfig, request)

    if (clearState) clearState()

    response = {
      hash,
      params: props,
      buyAmount: props.sellAmount,
      chains: retries.map(({ eid }) => eid),
      isSingleChain: retries.length === 0,
      action: 'retry' as NotificationActionTypes,
    } as TransactionResult
  } catch (e) {
    console.error(e)
    notifyTransactionError()
  }
  return response
}
